import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@journ/ui/accordion'
import { UserAvatar } from '@/components/user-avatar'
import { Badge } from '@journ/ui/badge'
import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { Sheet, SheetContent, SheetFooter, SheetHeader } from '@journ/ui/sheet'
import { useState } from 'react'
import { useSelector } from '@/libs/state'
import { useIntlayer } from 'react-intlayer'
import { useMember } from '../hooks/use-member'
import { useDepartments } from '../hooks/use-departments'
import { useMemberActions } from '../hooks/use-member-actions'
import type { EmergencyContact, MemberStatus } from '../domain/member'
import { usePermission } from '@/features/permissions/hooks/use-permission'
import { useDateFormat } from '@/features/preferences/hooks/use-date-format'
import { session$ } from '@/features/auth/store'
import { GLOBAL_SHEET_IDS, useSheet } from '@/features/ui/hooks/use-sheet'
import { openSheet } from '@/features/ui/store'
import { refreshMembersList } from '../store'

const statusVariant: Record<MemberStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
	active: 'default',
	on_leave: 'secondary',
	departed: 'outline',
	archived: 'outline',
}

type FieldProps = {
	label: string
	value: string | null | undefined
	colSpan?: boolean
}

const Field = ({ label, value, colSpan }: FieldProps) => (
	<div className={colSpan ? 'col-span-2' : undefined}>
		<div className='text-xs mb-2 text-606060'>{label}</div>
		<div className='text-[14px]'>{value || '-'}</div>
	</div>
)

type EmergencyContactFormState = {
	name: string
	relationship: string
	phone: string
	email: string
}

const emptyContactForm = (): EmergencyContactFormState => ({ name: '', relationship: '', phone: '', email: '' })

const MemberDetailsSheet = () => {
	const content = useIntlayer('hr-page')
	const { isOpen, entityId: viewMemberId, close: closeDetails } = useSheet(GLOBAL_SHEET_IDS.MEMBER_DETAILS)
	const { member, reload } = useMember(viewMemberId ?? null)
	const { formatPreferenceDate } = useDateFormat()
	const { departments } = useDepartments()
	const { addContact, removeContact } = useMemberActions()
	const user = useSelector(() => session$.user.get())
	const canManage = usePermission('hr.members.manage')
	const canEditOwn = usePermission('hr.members.edit-own')

	const [addingContact, setAddingContact] = useState(false)
	const [contactForm, setContactForm] = useState<EmergencyContactFormState>(emptyContactForm())
	const [contactErrors, setContactErrors] = useState<Partial<EmergencyContactFormState>>({})
	const [submittingContact, setSubmittingContact] = useState(false)

	const close = () => {
		setAddingContact(false)
		setContactForm(emptyContactForm())
		setContactErrors({})
		closeDetails()
	}

	const departmentName = member?.departmentId
		? (departments.find(d => d.id === member.departmentId)?.name ?? null)
		: null
	const canEditMember = canManage || (canEditOwn && user?.email === member?.email)

	const handleEdit = () => {
		if (!viewMemberId || !canEditMember) return
		closeDetails()
		openSheet(GLOBAL_SHEET_IDS.MEMBER_EDIT, viewMemberId)
	}

	const handleAddContact = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault()
		if (!canEditMember) return
		const errors: Partial<EmergencyContactFormState> = {}
		if (!contactForm.name.trim()) errors.name = content.emergencyContactForm.nameError.value
		if (!contactForm.relationship.trim()) errors.relationship = content.emergencyContactForm.relationshipError.value
		if (!contactForm.phone.trim()) errors.phone = content.emergencyContactForm.phoneError.value
		if (Object.keys(errors).length > 0) {
			setContactErrors(errors)
			return
		}
		setContactErrors({})
		setSubmittingContact(true)
		try {
			await addContact(viewMemberId!, {
				name: contactForm.name.trim(),
				relationship: contactForm.relationship.trim(),
				phone: contactForm.phone.trim(),
				email: contactForm.email.trim() || null,
			})
			setContactForm(emptyContactForm())
			setAddingContact(false)
			await reload()
			refreshMembersList()
		} finally {
			setSubmittingContact(false)
		}
	}

	const handleRemoveContact = async (contact: EmergencyContact) => {
		if (!viewMemberId || !canEditMember) return
		await removeContact(viewMemberId, contact.id)
		await reload()
		refreshMembersList()
	}

	const setContact = (key: keyof EmergencyContactFormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
		setContactForm(v => ({ ...v, [key]: e.target.value }))

	return (
		<Sheet open={isOpen} onOpenChange={close}>
			<SheetContent stack title={content.detailsSheet.title.value}>
				{member && (
					<div className='h-full flex flex-col min-h-0 -mx-6'>
						<div className='flex-1 overflow-y-auto scrollbar-hide min-h-0'>
							<SheetHeader className='flex justify-between items-center flex-row px-6 mb-4'>
								<div className='min-w-0 flex-1 flex items-center gap-3'>
									<UserAvatar name={member.fullName} avatarUrl={member.avatarUrl} size='lg' className='size-9' />
									<h2 className='text-lg font-serif truncate'>{member.fullName}</h2>
								</div>
								<Badge variant={statusVariant[member.status]}>
									{content.status[member.status]?.value ?? member.status}
								</Badge>
							</SheetHeader>

							{member.preferredName && <p className='px-6 text-[13px] text-606060 mb-4'>{member.preferredName}</p>}

							<div className='px-6'>
								<Accordion type='multiple' defaultValue={['identity', 'contact']} className='space-y-0'>
									<AccordionItem value='identity' className='border-b border-border'>
										<AccordionTrigger className='text-[16px] font-medium py-4'>
											{content.detailsSheet.identity}
										</AccordionTrigger>
										<AccordionContent>
											<div className='grid grid-cols-2 gap-4 pt-0'>
												<Field label={content.form.position.value} value={member.position} />
												<Field label={content.form.department.value} value={departmentName} />
												<Field
													label={content.form.employmentType.value}
													value={content.employmentType[member.employmentType]?.value ?? member.employmentType}
												/>
												<Field
													label={content.form.startDate.value}
													value={member.startDate ? formatPreferenceDate(member.startDate) : null}
												/>
												<Field
													label={content.form.defaultWorkSchedule.value}
													value={member.defaultWorkSchedule}
													colSpan
												/>
											</div>
										</AccordionContent>
									</AccordionItem>

									<AccordionItem value='contact' className='border-b border-border'>
										<AccordionTrigger className='text-base font-medium py-4'>
											{content.detailsSheet.contact}
										</AccordionTrigger>
										<AccordionContent>
											<div className='grid grid-cols-2 gap-4 pt-0'>
												<Field label={content.form.email.value} value={member.email} />
												<Field label={content.form.workPhone.value} value={member.workPhone} />
												<Field label={content.form.personalEmail.value} value={member.personalEmail} />
												<Field label={content.form.personalPhone.value} value={member.personalPhone} />
												<Field label={content.form.location.value} value={member.location} />
												<Field label={content.form.timezone.value} value={member.timezone} />
											</div>
										</AccordionContent>
									</AccordionItem>

									<AccordionItem value='address' className='border-b border-border'>
										<AccordionTrigger className='text-base font-medium py-4'>
											{content.detailsSheet.address}
										</AccordionTrigger>
										<AccordionContent>
											<div className='grid grid-cols-2 gap-4 pt-0'>
												<Field label={content.form.addressLine1.value} value={member.addressLine1} colSpan />
												<Field label={content.form.addressLine2.value} value={member.addressLine2} colSpan />
												<Field label={content.form.city.value} value={member.city} />
												<Field label={content.form.state.value} value={member.state} />
												<Field label={content.form.countryCode.value} value={member.countryCode} />
												<Field label={content.form.zip.value} value={member.zip} />
											</div>
										</AccordionContent>
									</AccordionItem>

									<AccordionItem value='emergency' className='border-b border-border'>
										<AccordionTrigger className='text-base font-medium py-4'>
											{content.detailsSheet.emergencyContacts}
										</AccordionTrigger>
										<AccordionContent>
											<div className='space-y-3'>
												{member.emergencyContacts.length === 0 && !addingContact && (
													<p className='text-[14px] text-606060'>{content.detailsSheet.noEmergencyContacts}</p>
												)}

												{member.emergencyContacts.map(contact => (
													<div key={contact.id} className='border border-border p-3 space-y-1'>
														<div className='flex items-start justify-between gap-2'>
															<div>
																<div className='text-[14px] font-medium'>{contact.name}</div>
																<div className='text-xs text-606060'>{contact.relationship}</div>
																<div className='text-xs text-606060'>{contact.phone}</div>
																{contact.email && <div className='text-xs text-606060'>{contact.email}</div>}
															</div>
															{canEditMember && (
																<Button
																	type='button'
																	size='sm'
																	variant='ghost'
																	onClick={() => handleRemoveContact(contact)}
																>
																	{content.detailsSheet.removeContact}
																</Button>
															)}
														</div>
													</div>
												))}

												{addingContact && (
													<form onSubmit={handleAddContact} className='border border-border p-3 space-y-3'>
														<div className='space-y-1.5'>
															<Label className='text-xs text-878787 font-normal'>
																{content.emergencyContactForm.name}
															</Label>
															<Input
																value={contactForm.name}
																onChange={setContact('name')}
																autoFocus
																autoComplete='off'
															/>
															{contactErrors.name && <p className='text-xs text-destructive'>{contactErrors.name}</p>}
														</div>
														<div className='space-y-1.5'>
															<Label className='text-xs text-878787 font-normal'>
																{content.emergencyContactForm.relationship}
															</Label>
															<Input
																value={contactForm.relationship}
																onChange={setContact('relationship')}
																autoComplete='off'
															/>
															{contactErrors.relationship && (
																<p className='text-xs text-destructive'>{contactErrors.relationship}</p>
															)}
														</div>
														<div className='space-y-1.5'>
															<Label className='text-xs text-878787 font-normal'>
																{content.emergencyContactForm.phone}
															</Label>
															<Input
																type='tel'
																value={contactForm.phone}
																onChange={setContact('phone')}
																autoComplete='off'
															/>
															{contactErrors.phone && <p className='text-xs text-destructive'>{contactErrors.phone}</p>}
														</div>
														<div className='space-y-1.5'>
															<Label className='text-xs text-878787 font-normal'>
																{content.emergencyContactForm.email}
															</Label>
															<Input
																type='email'
																value={contactForm.email}
																onChange={setContact('email')}
																autoComplete='off'
															/>
														</div>
														<div className='flex justify-end gap-2'>
															<Button
																type='button'
																size='sm'
																variant='ghost'
																onClick={() => {
																	setAddingContact(false)
																	setContactForm(emptyContactForm())
																	setContactErrors({})
																}}
															>
																{content.emergencyContactForm.cancel}
															</Button>
															<Button type='submit' size='sm' disabled={submittingContact}>
																{content.emergencyContactForm.add}
															</Button>
														</div>
													</form>
												)}

												{canEditMember && !addingContact && member.emergencyContacts.length < 5 && (
													<Button type='button' size='sm' variant='ghost' onClick={() => setAddingContact(true)}>
														<Icons.Add size={14} />
														{content.detailsSheet.addContact}
													</Button>
												)}
											</div>
										</AccordionContent>
									</AccordionItem>
								</Accordion>
							</div>
						</div>

						{canEditMember && (
							<SheetFooter className='border-t border-border pt-4 shrink-0'>
								<div className='w-full px-6 flex justify-end'>
									<Button onClick={handleEdit} variant='secondary' className='rounded-none'>
										{content.actions.edit}
									</Button>
								</div>
							</SheetFooter>
						)}
					</div>
				)}
			</SheetContent>
		</Sheet>
	)
}

export { MemberDetailsSheet }
